import dayjs, { type Dayjs } from "dayjs"
import { isEmpty, keyBy } from "lodash"
import { useMemo } from "react"

import {
  ATTENDANCE_STATUSES,
  TIME_BLOCKING_ATTENDANCE_STATUSES,
} from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import {
  type AddMakeupFormValues,
  type MakeupConstructor,
} from "#customTypes/composedTypes/makeupTypes"
import { type TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { type Uuid } from "#customTypes/globalTypes"
import {
  type Attendance,
  AttendanceStatus,
  invariant,
} from "#customTypes/types"
import { getSessionHeadcount, getSessionInfoFromId } from "#utils/classHelpers"
import { getNow } from "#utils/dateHelpers"

export const REASON_FOR_INVALID = {
  SIGNED_UP: "Student already signed up",
  ATTENDED: "Already attended",
  NOT_ENROLLED: "Student is not enrolled",
  PLANNED_ABSENT: "Already planned absent",
  CONFLICTING_CLASS: "Conflicting class",
  CONFLICTING_MAKEUP: "Conflicting makeup",
  MAKEUP_ALREADY_SCHEDULED: "Makeup already scheduled",
  SESSION_IN_PAST: "Session is in the past",
  SESSION_BEFORE_FILTER_DATE: "Session is before the selected date",
  ATTENDANCE_STATUS_NOT_FOUND: "Error: Attendance status not found",
  TRYOUT: "Tryout already scheduled",
  FULL: "Full",
} as const

const INVALID_ATTENDANCE_STATUSES: Record<AttendanceStatus, boolean> = {
  // Already attended
  [ATTENDANCE_STATUSES.PRESENT]: true,
  [ATTENDANCE_STATUSES.TARDY]: true,
  [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: true,
  [ATTENDANCE_STATUSES.TRYOUT_TARDY]: true,
  [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: true,
  [ATTENDANCE_STATUSES.MAKEUP_TARDY]: true,

  // Already have something scheduled
  [ATTENDANCE_STATUSES.PLANNED_ABSENCE]: true,

  // Should change original makeup
  [ATTENDANCE_STATUSES.MAKEUP_ABSENT]: true,
  [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: true,

  // Should change original tryout
  [ATTENDANCE_STATUSES.TRYOUT_ABSENT]: true,
  [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: true,

  // Invalid status is invalid -- maybe this should be allowed to overwrite bad statuses?
  [ATTENDANCE_STATUSES.INVALID]: true,

  // Kept in the list as a disabled option. Handled on its own so the label
  // is " - Not enrolled" rather than the generic attended reason.
  [ATTENDANCE_STATUSES.NOT_ENROLLED]: false,

  // Missed class or class hasn't happened yet
  [ATTENDANCE_STATUSES.ABSENT]: false, // Absent is a valid makeup reason
  [ATTENDANCE_STATUSES.ENROLLED]: false,
} as const

// The blocking pool is parsed to epoch ms ONCE per build and compared
// numerically. Constructing date objects per candidate-by-blocking pair was
// the measured hot spot (~380ms at 60x250 with moment); see the makeups skill.
interface TimeRange {
  id: Uuid
  start: number
  end: number
}

const toTimeRange = ({
  id,
  eventStart,
  eventEnd,
}: ClassSessionConstructor): TimeRange => ({
  id,
  start: Date.parse(eventStart),
  end: Date.parse(eventEnd),
})

// Strict on both sides so back-to-back sessions do not overlap
const isOverlapping = (start: number, end: number, other: TimeRange) =>
  start < other.end && other.start < end

// Makeups other than the one being edited, indexed by both session sides
const indexStudentMakeups = (
  studentMakeups: MakeupConstructor[] | undefined | null,
  initialValues: AddMakeupFormValues | undefined
) => {
  const otherMakeups = (studentMakeups ?? []).filter(
    (m) => !initialValues?.id || m.id !== initialValues.id
  )
  const scheduledMakeups = otherMakeups.filter((m) => !!m.makeupSessionId)

  return {
    makeupByMissedSessionId: keyBy(otherMakeups, "missedSessionId"),
    makeupByMakeupSessionId: keyBy(scheduledMakeups, "makeupSessionId"),
  }
}

export interface MakeupMissedSessionOptionsInput {
  missedClassId: Uuid | undefined
  missedClassSessions: ClassSessionConstructor[] | undefined | null
  studentAttendance: Attendance | undefined | null
  studentMakeups: MakeupConstructor[] | undefined | null
  studentTryouts: TryoutConstructor[] | undefined | null
  initialValues: AddMakeupFormValues | undefined
  isSummerCamp: boolean
}

export const buildMakeupMissedSessionOptions = ({
  missedClassId,
  missedClassSessions,
  studentAttendance,
  studentMakeups,
  studentTryouts,
  initialValues,
  isSummerCamp,
}: MakeupMissedSessionOptionsInput): DropdownOption[] => {
  if (!missedClassId || !missedClassSessions) return []

  invariant(missedClassId != null, "Missed class ID is required!")

  const tryoutBySessionId = keyBy(studentTryouts ?? [], "sessionId")
  const { makeupByMissedSessionId, makeupByMakeupSessionId } =
    indexStudentMakeups(studentMakeups, initialValues)
  // Match by sessionId. Array position is only the session index when the
  // attendance array is dense; a trimmed array would disable the wrong week.
  const attendanceBySessionId = keyBy(
    studentAttendance?.[missedClassId] ?? [],
    "sessionId"
  )

  const results: DropdownOption[] = []

  missedClassSessions
    .slice()
    .sort((a, b) => a.eventStart.localeCompare(b.eventStart))
    .forEach(({ id, eventStart }) => {
      const [, sessionIndex] = getSessionInfoFromId(id)
      let reasonForInvalid = ""
      const sessionAttendance = attendanceBySessionId[id]?.status

      if (!sessionAttendance) {
        reasonForInvalid = REASON_FOR_INVALID.ATTENDANCE_STATUS_NOT_FOUND
      } else if (sessionAttendance === ATTENDANCE_STATUSES.NOT_ENROLLED) {
        reasonForInvalid = REASON_FOR_INVALID.NOT_ENROLLED
      } else if (INVALID_ATTENDANCE_STATUSES[sessionAttendance]) {
        reasonForInvalid = REASON_FOR_INVALID.ATTENDED
      }

      // Student is already signed up for this tryout
      if (tryoutBySessionId[id]) {
        reasonForInvalid = REASON_FOR_INVALID.TRYOUT
      }

      // Student is already planned absent for this session
      if (makeupByMissedSessionId[id]) {
        reasonForInvalid = REASON_FOR_INVALID.PLANNED_ABSENT
      }

      // A makeup is already scheduled for this session
      if (makeupByMakeupSessionId[id]) {
        reasonForInvalid = REASON_FOR_INVALID.MAKEUP_ALREADY_SCHEDULED
      }

      const isCurrentSelection = id === initialValues?.missedSessionId
      const hasConflict = !isCurrentSelection && reasonForInvalid.length > 0
      const suffix = isCurrentSelection
        ? " - Current selection"
        : reasonForInvalid === REASON_FOR_INVALID.NOT_ENROLLED
          ? " - Not enrolled"
          : hasConflict
            ? ` (${reasonForInvalid})`
            : ""

      results.push({
        value: id,
        disabled: hasConflict,
        hasConflict,
        label: `${isSummerCamp ? "Day" : "Week"} ${sessionIndex + 1} - ${dayjs(eventStart).format("M/D/YY")}${suffix}`,
      })
    })

  return results
}

export const useMakeupMissedSessionOptions = ({
  missedClassId,
  missedClassSessions,
  studentAttendance,
  studentMakeups,
  studentTryouts,
  initialValues,
  isSummerCamp,
}: MakeupMissedSessionOptionsInput) => {
  return useMemo(
    () =>
      buildMakeupMissedSessionOptions({
        missedClassId,
        missedClassSessions,
        studentAttendance,
        studentMakeups,
        studentTryouts,
        initialValues,
        isSummerCamp,
      }),
    [
      missedClassSessions,
      studentAttendance,
      missedClassId,
      studentMakeups,
      studentTryouts,
      initialValues,
      isSummerCamp,
    ]
  )
}

export interface MakeupSessionOptionsInput {
  missedClassId: Uuid | undefined
  studentAttendance: Attendance | undefined | null
  studentMakeups: MakeupConstructor[] | undefined | null
  studentTryouts: TryoutConstructor[] | undefined | null
  studentClassSessions: ClassSessionConstructor[] | undefined | null
  possibleMakeupClasses: ClassConstructor[] | undefined | null
  possibleMakeupClassSessions: ClassSessionConstructor[] | undefined | null
  initialValues: AddMakeupFormValues | undefined
  makeupFilterDate: Dayjs | null | undefined
}

export const buildMakeupSessionOptions = ({
  missedClassId,
  studentClassSessions,
  possibleMakeupClasses,
  possibleMakeupClassSessions,
  makeupFilterDate,
  studentAttendance,
  studentMakeups,
  studentTryouts,
  initialValues,
}: MakeupSessionOptionsInput): DropdownOption[] => {
  if (
    !missedClassId ||
    !studentClassSessions?.length ||
    !possibleMakeupClassSessions?.length
  )
    return []

  const classesById = keyBy(possibleMakeupClasses, "id")
  const sessionsById = keyBy(possibleMakeupClassSessions, "id")

  const sortedSessions = Object.values(sessionsById).sort((a, b) =>
    a.eventStart.localeCompare(b.eventStart)
  )

  if (isEmpty(sessionsById)) return []

  invariant(missedClassId != null, "Missed class ID is required!")

  const nowMs = getNow().valueOf()
  const filterDateMs = makeupFilterDate
    ? dayjs(makeupFilterDate).valueOf()
    : null

  const tryoutBySessionId = keyBy(studentTryouts ?? [], "sessionId")
  const { makeupByMissedSessionId, makeupByMakeupSessionId } =
    indexStudentMakeups(studentMakeups, initialValues)

  // Sessions the student is booked into via another makeup (excluded from the
  // class pool so they keep the more specific "Makeup already scheduled" reason)
  const existingMakeupSessionIds = new Set(Object.keys(makeupByMakeupSessionId))

  const blockingClassSessions: TimeRange[] = []
  const existingMakeupSessions: TimeRange[] = []
  studentClassSessions.forEach((session) => {
    if (existingMakeupSessionIds.has(session.id)) {
      existingMakeupSessions.push(toTimeRange(session))
      return
    }

    const [studentClassId, studentSessionIndex] = getSessionInfoFromId(
      session.id
    )
    const status =
      studentAttendance?.[studentClassId]?.[studentSessionIndex]?.status
    if (!status || !TIME_BLOCKING_ATTENDANCE_STATUSES[status]) return

    blockingClassSessions.push(toTimeRange(session))
  })

  const results: DropdownOption[] = []

  sortedSessions.forEach(({ id, eventStart, eventEnd, classId }) => {
    let reasonForInvalid = ""
    let warningReason = ""

    const candidateStart = Date.parse(eventStart)
    const candidateEnd = Date.parse(eventEnd)

    // Don't need to check attendance because we filter out sessions in the past
    const [, sessionIndex] = getSessionInfoFromId(id)
    const sessionAttendance =
      studentAttendance?.[classId]?.[sessionIndex]?.status

    // Student is already signed up for this session
    if (sessionAttendance && INVALID_ATTENDANCE_STATUSES[sessionAttendance]) {
      reasonForInvalid = REASON_FOR_INVALID.SIGNED_UP
    }

    // Filter out sessions before the filter date
    const isBeforeFilterDate =
      filterDateMs !== null && candidateStart < filterDateMs

    if (isBeforeFilterDate && id !== initialValues?.makeupSessionId) {
      return
    }

    const classObj = classesById[classId]
    const headcount = getSessionHeadcount(sessionsById[id])
    const capacity = Number(classObj?.capacity) || null

    // Warning, not a conflict: the front desk may overbook deliberately.
    // Mobile hard-blocks full sessions instead (see mobile-partner-api).
    if (capacity !== null && headcount >= capacity) {
      warningReason = REASON_FOR_INVALID.FULL
    }

    // Past wins over full: the headcount is already visible in the label.
    if (candidateStart < nowMs) {
      warningReason = REASON_FOR_INVALID.SESSION_IN_PAST
    }

    // Student is already signed up for this tryout
    if (tryoutBySessionId[id]) {
      reasonForInvalid = REASON_FOR_INVALID.TRYOUT
    }

    // Student is already planned absent for this session
    if (makeupByMissedSessionId[id]) {
      reasonForInvalid = REASON_FOR_INVALID.PLANNED_ABSENT
    }

    // A makeup is already scheduled for this session
    if (makeupByMakeupSessionId[id]) {
      reasonForInvalid = REASON_FOR_INVALID.MAKEUP_ALREADY_SCHEDULED
    }

    // Student has a conflicting class (excludes sessions that are existing makeup destinations)
    const isConflictingClass = blockingClassSessions.some((blocking) =>
      isOverlapping(candidateStart, candidateEnd, blocking)
    )

    if (isConflictingClass) {
      reasonForInvalid = REASON_FOR_INVALID.CONFLICTING_CLASS
    }

    // Time conflict with an existing makeup session (not the exact session match)
    const isConflictingMakeupTime = existingMakeupSessions.some(
      (makeupSession) =>
        makeupSession.id !== id &&
        isOverlapping(candidateStart, candidateEnd, makeupSession)
    )

    if (isConflictingMakeupTime) {
      reasonForInvalid = REASON_FOR_INVALID.CONFLICTING_MAKEUP
    }

    if (!classObj?.isMakeupAllowed) {
      reasonForInvalid = "Makeup not allowed"
    }

    const isCurrentSelection = id === initialValues?.makeupSessionId
    const hasConflict = !isCurrentSelection && reasonForInvalid.length > 0
    const hasWarning = !isCurrentSelection && !hasConflict && !!warningReason
    const annotation = isCurrentSelection
      ? " - Current selection"
      : hasConflict
        ? ` (${reasonForInvalid})`
        : hasWarning
          ? ` (${warningReason})`
          : ""

    results.push({
      value: `${classId} ${id}`,
      disabled: hasConflict,
      hasConflict,
      hasWarning,
      label: `${classObj?.name} - ${dayjs(eventStart).format(
        "M/D/YY h:mm a"
      )} - ${headcount}/${classObj?.capacity}${annotation}`,
    })
  })

  return results
}

export const useMakeupSessionOptions = ({
  missedClassId,
  studentClassSessions,
  possibleMakeupClasses,
  possibleMakeupClassSessions,
  makeupFilterDate,
  studentAttendance,
  studentMakeups,
  studentTryouts,
  initialValues,
}: MakeupSessionOptionsInput) => {
  return useMemo(
    () =>
      buildMakeupSessionOptions({
        missedClassId,
        studentClassSessions,
        possibleMakeupClasses,
        possibleMakeupClassSessions,
        makeupFilterDate,
        studentAttendance,
        studentMakeups,
        studentTryouts,
        initialValues,
      }),
    [
      studentAttendance,
      missedClassId,
      studentMakeups,
      studentTryouts,
      studentClassSessions,
      possibleMakeupClasses,
      possibleMakeupClassSessions,
      makeupFilterDate,
      initialValues,
    ]
  )
}
